import React from 'react'
import { Check, Loader2, Mail, Printer, Receipt, RotateCcw } from 'lucide-react'
import type { CompletedTradeData } from './types'
import { PAYMENT_METHODS } from './tradePaymentMethods'
import { formatCompletedTradeRefLabel } from './helpers'
import styles from '../CreateTransaction.module.css'

type TradeReceiptProps = {
  data: CompletedTradeData
  formatPrice: (cents: number) => string
  receiptLocale: string
  storeName: string | undefined
  receiptNumberLoading: boolean
  getTradeTypeLabel: (type: 'trade' | 'buy' | 'sell') => string
  onPrint80: () => void
  onPrint58: () => void
  onEmail: () => void
  onNewTransaction: () => void
}

export const TradeReceipt: React.FC<TradeReceiptProps> = ({
  data,
  formatPrice,
  receiptLocale,
  storeName,
  receiptNumberLoading,
  getTradeTypeLabel,
  onPrint80,
  onPrint58,
  onEmail,
  onNewTransaction,
}) => {
  const diff = data.differenceValueCents
  const isEven = diff === 0
  const customerOwes = diff > 0
  const completedDate = new Date(data.completedAt)

  const paymentLabel = PAYMENT_METHODS.find((m) => m.value === data.paymentMethod)?.label || data.paymentMethod

  const totalStoreQty = data.storeItems.reduce((sum, i) => sum + i.quantity, 0)
  const totalCustomerQty = data.customerItems.reduce((sum, i) => sum + i.quantity, 0)

  const receiptRefLabel = formatCompletedTradeRefLabel(data)

  return (
    <div className={styles.receiptContainer} id="trade-receipt">
      <div className={styles.receiptCard}>
        <div className={styles.receiptHeader}>
          <div className={styles.receiptSuccessIcon}>
            <Check size={28} />
          </div>
          <h2 className={styles.receiptTitle}>In-Store Transaction Completed</h2>
          {storeName && <p className={styles.receiptStoreName}>{storeName}</p>}
          <div className={styles.receiptMeta}>
            <span>
              {getTradeTypeLabel(data.type)} {receiptRefLabel}
            </span>
            <span>
              {completedDate.toLocaleDateString(receiptLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })}{' '}
              {completedDate.toLocaleTimeString(receiptLocale, { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          {receiptNumberLoading && (
            <div className={styles.receiptRefetchNote}>
              <Loader2 className={styles.spinner} size={14} aria-hidden />
              Fetching official receipt number…
            </div>
          )}
        </div>

        {(data.customerName || data.customerEmail || data.customerPhone) && (
          <div className={styles.receiptSection}>
            <h3 className={styles.receiptSectionTitle}>Customer</h3>
            <div className={styles.receiptCustomer}>
              {data.customerName && <span className={styles.receiptCustomerName}>{data.customerName}</span>}
              {data.customerEmail && <span className={styles.receiptCustomerDetail}>{data.customerEmail}</span>}
              {data.customerPhone && <span className={styles.receiptCustomerDetail}>{data.customerPhone}</span>}
            </div>
          </div>
        )}

        {data.storeItems.length > 0 && (
          <div className={styles.receiptSection}>
            <h3 className={styles.receiptSectionTitle}>Store Items ({totalStoreQty})</h3>
            <div className={styles.receiptItemList}>
              {data.storeItems.map((item) => (
                <div key={item.id} className={styles.receiptItem}>
                  <div className={styles.receiptItemInfo}>
                    <span className={styles.receiptItemName}>
                      {item.reversesItemId && <span className={styles.receiptItemQty}>Returned </span>}
                      {Math.abs(item.quantity) > 1 && <span className={styles.receiptItemQty}>{Math.abs(item.quantity)}× </span>}
                      {item.itemName}
                    </span>
                    <span className={styles.receiptItemMeta}>
                      {[item.printing, item.condition, item.setName].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  <span className={styles.receiptItemPrice}>{formatPrice(item.totalValueCents)}</span>
                </div>
              ))}
              <div className={styles.receiptSubtotal}>
                <span>Subtotal</span>
                <span>{formatPrice(data.storeValueCents)}</span>
              </div>
            </div>
          </div>
        )}

        {data.customerItems.length > 0 && (
          <div className={styles.receiptSection}>
            <h3 className={styles.receiptSectionTitle}>Customer Items ({totalCustomerQty})</h3>
            <div className={styles.receiptItemList}>
              {data.customerItems.map((item) => (
                <div key={item.id} className={styles.receiptItem}>
                  <div className={styles.receiptItemInfo}>
                    <span className={styles.receiptItemName}>
                      {item.quantity > 1 && <span className={styles.receiptItemQty}>{item.quantity}× </span>}
                      {item.itemName}
                    </span>
                    <span className={styles.receiptItemMeta}>
                      {[item.printing, item.condition, item.setName].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  <span className={styles.receiptItemPrice}>{formatPrice(item.totalValueCents)}</span>
                </div>
              ))}
              <div className={styles.receiptSubtotal}>
                <span>Subtotal</span>
                <span>{formatPrice(data.customerValueCents)}</span>
              </div>
            </div>
          </div>
        )}

        <div className={styles.receiptSummary}>
          <div className={styles.receiptSummaryRow}>
            <span>Store Total</span>
            <span>{formatPrice(data.storeValueCents)}</span>
          </div>
          <div className={styles.receiptSummaryRow}>
            <span>Customer Total</span>
            <span>{formatPrice(data.customerValueCents)}</span>
          </div>
          <div className={styles.receiptDivider} />
          <div className={`${styles.receiptSummaryRow} ${styles.receiptSummaryTotal}`}>
            <span>Difference</span>
            <span className={isEven ? styles.even : customerOwes ? styles.customerOwes : styles.storeOwes}>
              {isEven ? 'Even Trade' : customerOwes ? `Customer paid ${formatPrice(diff)}` : `Store paid ${formatPrice(Math.abs(diff))}`}
            </span>
          </div>
          {!isEven && (
            <div className={styles.receiptSummaryRow}>
              <span>Payment</span>
              <span>{paymentLabel}</span>
            </div>
          )}
        </div>

        {data.notes && (
          <div className={styles.receiptNotes}>
            <strong>Notes:</strong> {data.notes}
          </div>
        )}

        <div className={styles.receiptPrintFooter}>Powered by TCGEasy</div>

        <div className={styles.receiptActions}>
          <div className={styles.receiptActionsRow}>
            <button type="button" className={styles.receiptActionBtn} onClick={onPrint80} title="Generate an 80mm receipt PDF">
              <Printer size={16} />
              Receipt 80mm
            </button>
            <button type="button" className={styles.receiptActionBtn} onClick={onPrint58} title="Generate a 58mm receipt PDF">
              <Receipt size={16} />
              Receipt 58mm
            </button>
            <button type="button" className={styles.receiptActionBtn} onClick={onEmail} title="Email receipt to your store (and to the customer email on file, if any)">
              <Mail size={16} />
              Email
            </button>
          </div>
          <button type="button" className={styles.receiptNewBtn} onClick={onNewTransaction}>
            <RotateCcw size={16} />
            New Transaction
          </button>
        </div>
      </div>
    </div>
  )
}
